import { Timeslot, TipologiaVisita, VisitorsInfoResponse } from '@Interfaces/interfaces';
import { Museum } from '@Interfaces/dddApi.interfaces';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { finalize, Observable, shareReplay } from 'rxjs';
import { LanguageService } from './language.service';
import { APP_CONFIG } from '@Core/config/app-config';

type MuseumOffersResponse = {
  result: any;
  values: TipologiaVisita[];
  captchaEnabled: boolean;
};
type PublishedMuseumResponse = {
  count: number;
  next: any | null;
  previous: any | null;
  results: Museum[];
};
type TimeslotResponse = {
  result: {
    code:string;
    desc:string;
    success:boolean
  };
  ticketType:string;
  offers:Timeslot[]
}

@Injectable({
  providedIn: 'root',
})
export class MuseumService {

  private readonly config = inject(APP_CONFIG);

  private _zoneCodesrInFlightRequest: Observable<MuseumOffersResponse> | null = null;
  private _inFlightCodesr: string | null = null;

  constructor(
    private http: HttpClient,
    private languageService: LanguageService
  ) {}

  /**
   * Get all the published museums, able to search and paginate.
   * (DotDotDot API)
   * @param search string optional
   * @param page number optional - page number ( >0 )!!
   * @param pageSize number optional
   * @returns Observable<PublishedMuseumResponse>
   */
  public getAllPublishedMuseums(
    search?: string,
    page?: number,
    pageSize?: number
  ): Observable<PublishedMuseumResponse> {
    let params = new HttpParams();

    if (search) {
      params = params.append('search', search);
    }
    if (page) {
      params = params.append('page', page.toString());
    }
    if (pageSize) {
      params = params.append('page_size_unused', pageSize.toString());
    }

    return this.http.get<PublishedMuseumResponse>(
      this.config.dotDotDotApiUrl + '/public/cached/museum/',
      { params }
    );
  }

  /**
   * Returns all the info for the specified Museum.
   * (DotDotDot API)
   * @param codesr codice estero Museo
   * @returns { Observable<Museum> } Informazioni Museo
   */
  public getSingleMuseum(codesr: string): Observable<Museum> {
    return this.http.get<Museum>(
      this.config.dotDotDotApiUrl + `/public/cached/museum/${codesr}/`
    );
  }

  /**
   * Recupera la lista di offerte disponibili per il dato Museo
   * @param codesr - codice museo (es. 137 -> Pantheon)
   * @returns Lista di offerte disponibili
   */
  public getMuseumOffers(codesr: string): Observable<MuseumOffersResponse> {

    if(this._zoneCodesrInFlightRequest && this._inFlightCodesr === codesr){
      return this._zoneCodesrInFlightRequest;
    }

    const headers: HttpHeaders = new HttpHeaders()
      .set('lang', this.languageService.getValidLanguageValue().lang_code)
      .set('module', '6')
      .set('channel', 'b2c');

    const request = this.http.get<MuseumOffersResponse>(
      this.config.apiUrl + `/zone/codesr/${codesr}`,
      { headers: headers }
    ).pipe(
      shareReplay(1),
      finalize(() => {
        this._zoneCodesrInFlightRequest = null;
        this._inFlightCodesr = null;
      })
    );

    this._zoneCodesrInFlightRequest = request;
    this._inFlightCodesr = codesr;

    return request
  }

  /**
   * Recupera la singola offerta
   * @param idezon - id offerta
   * @returns Singola offerta
   */
  public getOffer(idezon: number): Observable<MuseumOffersResponse> {
    const headers: HttpHeaders = new HttpHeaders()
      .set('lang', this.languageService.getValidLanguageValue().lang_code)
      .set('module', '6')
      .set('channel', 'b2c')
      .set('token', localStorage.getItem('loginToken') ?? '');

    return this.http.get<MuseumOffersResponse>(
      this.config.apiUrl + `/zone/${idezon}`,
      { headers: headers }
    );
  }

  /**
   * Recupera i timeslot disponibili per una data offerta in un dato museo
   * per una specifica giornata.
   * @param codveo - codice museo (es. 137 -> Pantheon)
   * @param idezon - codice offerta
   * @param date - data formato YYYY-MM-DD
   * @returns timeslot disponibili per la giornata
   */
  public getOfferTimeslots(codveo:number, idezon:number, date:string):Observable<TimeslotResponse>{

    let params = new HttpParams()
      .append('codveo', codveo)
      .append('idezon', idezon)
      .append('date', date);

    const headers: HttpHeaders = new HttpHeaders()
      .set('lang', this.languageService.getValidLanguageValue().lang_code)
      .set('module', '6')
      .set('channel', 'b2c');

    return this.http.get<TimeslotResponse>(
      this.config.apiUrl + `/offer/timeslot`,
      { params:params, headers: headers }
    );
  }

  /**
   * Recupera info del museo da portal-web per
   * modale "informazioni per i visitatori"
   * @param codesr codice estero Museo
   * @returns { Observable<VisitorsInfoResponse> } Informazioni Visitatori
   */
  public getMuseumVisitorsInfo(codesr:string): Observable<VisitorsInfoResponse> {

    const headers: HttpHeaders = new HttpHeaders()
      .set('lang', this.languageService.getValidLanguageValue().lang_code)
      .set('module', '6')
      .set('channel', 'b2c');

    return this.http.get<VisitorsInfoResponse>(
      this.config.apiUrl + `/museum/details/${codesr}`,
      {  headers: headers }
    );
  }
}
